import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTA from "@/components/CTA";
import Seo from "@/components/Seo";
import { CaretRight, Warning, Check } from "@phosphor-icons/react";
import { trackViewIndustry } from "@/lib/analytics";

export function IndustriesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/industries").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="industries-index">
      <Seo title="Industries — Surveillance for Every Site Type" description="Solar surveillance solutions for construction, commercial property, auto dealerships, equipment yards, utilities, government, parking lots, vacant properties, events and critical infrastructure." path="/industries" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Industries" }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">Industries</div>
        <h1 className="mb-4">The sites and industries we protect.</h1>
        <p className="text-white/75 text-lg max-w-2xl">From active construction to critical infrastructure, we support the properties where surveillance has to move with the work.</p>
      </section>
      <section className="container-x pb-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-chrome border border-chrome">
        {items.map((ind) => (
          <Link key={ind.slug} to={`/industries/${ind.slug}`} data-testid={`industry-card-${ind.slug}`} className="bg-obsidian hover:bg-steel transition-colors group overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-steel">
              <img src={ind.image} alt={ind.short} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity" />
            </div>
            <div className="p-6">
              <div className="font-display uppercase text-xl font-bold mb-2 group-hover:text-solar transition-colors">{ind.title}</div>
              <div className="text-white/70 text-sm leading-relaxed">{ind.summary}</div>
            </div>
          </Link>
        ))}
      </section>
      <CTA />
    </div>
  );
}

export function IndustryDetail() {
  const { slug } = useParams();
  const [ind, setInd] = useState(null);
  const [systems, setSystems] = useState([]);
  useEffect(() => {
    api.get(`/industries/${slug}`).then((r) => {
      setInd(r.data);
      trackViewIndustry(r.data);
    }).catch(() => setInd(false));
    api.get("/systems").then((r) => setSystems(r.data));
  }, [slug]);
  if (ind === false) return <div className="container-x py-20"><h1>Industry not found</h1></div>;
  if (!ind) return <div className="container-x py-20 text-white/60">Loading…</div>;
  const related = systems.filter((s) => (ind.recommended_systems || []).includes(s.slug));

  return (
    <div data-testid={`industry-detail-${slug}`}>
      <Seo
        title={ind.title}
        description={ind.summary}
        path={`/industries/${slug}`}
        ogImage={ind.image}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Industries", path: "/industries" }, { label: ind.title }]}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Industries", to: "/industries" }, { label: ind.title }]} /></section>
      <section className="relative overflow-hidden border-b border-chrome">
        <img src={ind.image} alt={ind.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-obsidian/30" />
        <div className="relative container-x py-16 md:py-24">
          <div className="eyebrow text-solar mb-3">Industry</div>
          <h1 className="mb-5 max-w-3xl">{ind.title}</h1>
          <p className="text-white/85 text-lg max-w-2xl leading-relaxed">{ind.summary}</p>
        </div>
      </section>

      <section className="container-x py-16 grid lg:grid-cols-2 gap-10">
        <div className="border border-chrome bg-steel p-8">
          <div className="eyebrow text-solar mb-4 flex items-center gap-2"><Warning size={16} weight="fill" /> Common Risks</div>
          <ul className="space-y-3">
            {(ind.risks || []).map((r) => (
              <li key={r} className="flex items-start gap-3 text-white/85">
                <span className="w-1.5 h-1.5 bg-solar mt-2 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-chrome bg-steel p-8">
          <div className="eyebrow text-solar mb-4 flex items-center gap-2"><Check size={16} weight="bold" /> How We Help</div>
          <p className="text-white/85 leading-relaxed mb-4">
            We deploy solar-powered surveillance sized to your site's layout and risk profile. Coverage depends on placement, unit count and configuration —
            every quote begins with a site evaluation.
          </p>
          <ul className="space-y-3">
            {[
              "Solar-powered — no permanent utilities needed",
              "Cellular connectivity — no site Wi-Fi required",
              "Rapid deployment — typically 24–72 hours",
              "Remote live and recorded video access",
              "Visible deterrence and event alerts",
              "Multi-unit deployment for larger footprints",
            ].map((c) => (
              <li key={c} className="flex items-start gap-3 text-white/85">
                <Check size={18} weight="bold" className="text-solar mt-1 shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-steel py-16 border-y border-chrome">
          <div className="container-x">
            <div className="eyebrow text-solar mb-3">Recommended Systems</div>
            <h2 className="mb-8">Configurations we deploy for this industry.</h2>
            <div className="grid md:grid-cols-3 gap-px bg-chrome border border-chrome">
              {related.map((sys) => (
                <Link key={sys.slug} to={`/systems/${sys.slug}`} className="bg-obsidian p-6 hover:bg-steel transition-colors">
                  <div className="eyebrow text-solar mb-2">{sys.name}</div>
                  <div className="font-display uppercase text-xl font-bold mb-2">{sys.short_name}</div>
                  <div className="text-white/70 text-sm mb-4 leading-relaxed">{sys.summary?.substring(0, 120)}…</div>
                  <span className="text-solar text-sm font-bold uppercase tracking-wider flex items-center gap-1">View <CaretRight size={12} weight="bold" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTA />
    </div>
  );
}
